import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableBody, TableCell, TableHead, TableRow,
  Button, Alert, IconButton, Chip, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, FormControlLabel, Switch
} from '@mui/material';
import { Numbers, Home, Add, Edit, Delete, WarningAmber } from '@mui/icons-material';
import axios from 'axios';
import { useUnsavedGuard } from '../hooks/useUnsavedGuard';

const emptyForm = {
  code: '',
  description: '',
  prefix: '',
  padding: 6,
  lastNoUsed: 0,
  isActive: true
};

const NumberSeriesList = () => {
  const navigate = useNavigate();
  const [series, setSeries] = useState([]);
  const [error, setError] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCode, setEditingCode] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const { attemptClose, discardDialog } = useUnsavedGuard({ open: dialogOpen, value: form, onClose: () => setDialogOpen(false) });

  // Deactivate confirm dialog
  const [deactivateDlg, setDeactivateDlg] = useState(null);

  const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };

  const fetchSeries = async () => {
    try {
      const { data } = await axios.get(
        `${process.env.REACT_APP_API_URL}/api/setup/number-series`,
        { headers }
      );
      setSeries(data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load number series');
    }
  };

  useEffect(() => {
    fetchSeries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCreate = () => {
    setEditingCode(null);
    setForm(emptyForm);
    setDialogOpen(true);
  };

  const handleEdit = (s) => {
    setEditingCode(s.code);
    setForm({
      code: s.code,
      description: s.description,
      prefix: s.prefix,
      padding: s.padding,
      lastNoUsed: s.lastNoUsed,
      isActive: s.isActive
    });
    setDialogOpen(true);
  };

  const confirmAction = async () => {
    try {
      await axios.delete(
        `${process.env.REACT_APP_API_URL}/api/setup/number-series/${deactivateDlg}`,
        { headers }
      );
      setDeactivateDlg(null);
      setError('Number series deactivated successfully');
      fetchSeries();
    } catch (err) {
      setDeactivateDlg(null);
      setError(err.response?.data?.error || 'Failed to deactivate number series');
    }
  };

  const handleSave = async () => {
    try {
      if (editingCode) {
        await axios.put(
          `${process.env.REACT_APP_API_URL}/api/setup/number-series/${editingCode}`,
          {
            description: form.description,
            prefix: form.prefix,
            padding: Number(form.padding),
            lastNoUsed: Number(form.lastNoUsed),
            isActive: form.isActive
          },
          { headers }
        );
      } else {
        await axios.post(
          `${process.env.REACT_APP_API_URL}/api/setup/number-series`,
          {
            code: form.code,
            description: form.description,
            prefix: form.prefix,
            padding: Number(form.padding),
            lastNoUsed: Number(form.lastNoUsed),
            isActive: form.isActive
          },
          { headers }
        );
      }
      setDialogOpen(false);
      setError('');
      fetchSeries();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save number series');
    }
  };

  const previewNo = (s) => `${s.prefix}${String(s.lastNoUsed + 1).padStart(s.padding, '0')}`;

  return (
    <Box sx={{ maxWidth: 1000, mx: 'auto', mt: 4 }}>
      <Card elevation={3}>
        <CardContent>
          <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
            <Box display="flex" alignItems="center">
              <Numbers sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" color="primary">
                Number Series
              </Typography>
            </Box>
            <Box>
              <IconButton
                color="primary"
                onClick={() => navigate('/setups')}
                title="Back to Setups"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' }, mr: 1 }}
              >
                <Home />
              </IconButton>
              <IconButton
                color="primary"
                onClick={handleCreate}
                title="Create New Series"
                sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}
              >
                <Add />
              </IconButton>
            </Box>
          </Box>

          {error && <Alert severity={error.includes('success') ? 'success' : 'error'} sx={{ mb: 2 }}>{error}</Alert>}

          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Code</strong></TableCell>
                <TableCell><strong>Description</strong></TableCell>
                <TableCell><strong>Prefix</strong></TableCell>
                <TableCell align="right"><strong>Last No. Used</strong></TableCell>
                <TableCell><strong>Next No.</strong></TableCell>
                <TableCell><strong>Status</strong></TableCell>
                <TableCell><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {series.map((s) => (
                <TableRow key={s.code} hover>
                  <TableCell>{s.code}</TableCell>
                  <TableCell>{s.description}</TableCell>
                  <TableCell>{s.prefix}</TableCell>
                  <TableCell align="right">{s.lastNoUsed}</TableCell>
                  <TableCell>{previewNo(s)}</TableCell>
                  <TableCell>
                    <Chip
                      label={s.isActive ? 'Active' : 'Inactive'}
                      color={s.isActive ? 'success' : 'default'}
                      size="small"
                      variant="outlined"
                    />
                  </TableCell>
                  <TableCell>
                    <IconButton
                      color="primary"
                      onClick={() => handleEdit(s)}
                      title="Edit Series"
                      sx={{ mr: 1 }}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="warning"
                      onClick={() => setDeactivateDlg(s.code)}
                      title="Deactivate Series"
                      disabled={!s.isActive}
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Deactivate Confirm Dialog */}
      <Dialog open={Boolean(deactivateDlg)} onClose={() => setDeactivateDlg(null)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'warning.main' }}>
          <WarningAmber color="warning" /> Deactivate Number Series
        </DialogTitle>
        <DialogContent>
          <Typography>Deactivate series <strong>{deactivateDlg}</strong>? Documents using this series will keep their numbers.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeactivateDlg(null)}>Cancel</Button>
          <Button variant="contained" color="warning" onClick={confirmAction}>Deactivate</Button>
        </DialogActions>
      </Dialog>

      {discardDialog}
      <Dialog open={dialogOpen} onClose={attemptClose} fullWidth maxWidth="sm">
        <DialogTitle>{editingCode ? `Edit Number Series: ${editingCode}` : 'New Number Series'}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Code"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              disabled={!!editingCode}
              helperText="Short unique code, e.g. INV, REC, GL, JNL, BL"
              fullWidth
            />
            <TextField
              label="Description"
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              fullWidth
            />
            <TextField
              label="Prefix"
              value={form.prefix}
              onChange={(e) => setForm({ ...form, prefix: e.target.value })}
              helperText="Text shown before the number, e.g. INV-"
              fullWidth
            />
            <TextField
              label="Padding"
              type="number"
              value={form.padding}
              onChange={(e) => setForm({ ...form, padding: e.target.value })}
              helperText="Number of digits, e.g. 6 -> 000001"
              fullWidth
            />
            <TextField
              label="Last No. Used"
              type="number"
              value={form.lastNoUsed}
              onChange={(e) => setForm({ ...form, lastNoUsed: e.target.value })}
              helperText="Next number issued will be this value + 1"
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={attemptClose}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default NumberSeriesList;
